import { Card, Flex, Typography, theme } from "antd";
import type React from "react";

interface SegmentedOption<T extends string> {
	value: T;
	label: string;
	description?: string;
	disabled?: boolean;
	/** Optional icon rendered before the label. */
	icon?: React.ReactNode;
}

interface SegmentedSelectionProps<T extends string> {
	options: SegmentedOption<T>[];
	/** Injected by antd Form.Item when used with `name` — can also be set directly. */
	value?: T;
	disabled?: boolean;
	onChange?: (value: T) => void;
	columns?: number;
	showCheck?: boolean;
	/** Injected by antd Form.Item — "error" shows a red border + error text. */
	status?: "" | "error" | "warning";
	/** Error message shown below the grid when status is "error". */
	error?: string;
}

/** A grid of selectable card options, like a visual radio group.
 *  Compatible with antd Form.Item — value, onChange, and status are injected automatically. */
export function SegmentedSelection<T extends string>({
	options,
	value,
	onChange,
	columns = 2,
	showCheck = false,
	status,
	error,
}: SegmentedSelectionProps<T>) {
	const { token } = theme.useToken();
	const hasError = status === "error";

	const containerStyle: React.CSSProperties = hasError
		? {
				display: "grid",
				gridTemplateColumns: `repeat(${columns}, 1fr)`,
				gap: 8,
				padding: 4,
				borderRadius: token.borderRadius,
				border: `1px solid ${token.colorError}`,
			}
		: {
				display: "grid",
				gridTemplateColumns: `repeat(${columns}, 1fr)`,
				gap: 8,
			};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
			<div style={containerStyle}>
				{options.map((option) => {
					const selected = option.value === value;
					const disabled = option.disabled ?? false;
					return (
						// ponytail: wrapper clips the corner triangle at the card's border-radius
						<div
							key={option.value}
							style={{
								position: "relative",
								opacity: disabled ? 0.45 : 1,
								// overflow: "hidden",
								// borderRadius: token.borderRadius,
							}}
						>
							<Card
								hoverable={!disabled}
								size="small"
								onClick={() => !disabled && onChange?.(option.value)}
								style={{
									height: "100%",
									cursor: disabled ? "not-allowed" : "pointer",
									borderColor: selected
										? token.colorPrimary
										: token.colorBorder,
									backgroundColor: selected ? token.colorPrimaryBg : undefined,
									transition: "border-color 0.2s, background-color 0.2s",
								}}
							>
								<Flex align="center" gap={8}>
									{option.icon}
									<Typography.Text strong>{option.label}</Typography.Text>
								</Flex>
								{option.description && (
									<div
										style={{ color: token.colorTextSecondary, marginTop: 2 }}
									>
										{option.description}
									</div>
								)}
							</Card>
							{selected && showCheck && (
								<span
									style={{
										position: "absolute",
										top: 6,
										right: 6,
										width: 16,
										height: 16,
										borderRadius: "50%",
										background: token.colorPrimary,
										color: "#fff",
										fontSize: 10,
										fontWeight: 700,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										pointerEvents: "none",
										zIndex: 1,
									}}
								>
									✓
								</span>
							)}
						</div>
					);
				})}
			</div>
			{hasError && error && (
				<Typography.Text type="danger" style={{ fontSize: token.fontSizeSM }}>
					{error}
				</Typography.Text>
			)}
		</div>
	);
}
